<template>
  <div v-if="user" class="subscription-page">
    <b-button
      v-if="!isImpersonating && !isNitroSubscriber"
      variant="primary"
      class="d-block logout-button"
      @click.prevent="logout"
    >
      <span>LogOut</span>
    </b-button>
    <div v-if="isImpersonating && !user.subscribed" class="text-center mb-4">
      <button
        class="btn btn-outline-dark btn-sm"
        :disabled="loadingStopImpersonate"
        @click="stopImpersonate"
      >
        <b-spinner v-if="loadingStopImpersonate" small />
        <i class="uil uil-sign-in-alt"></i> Main account
      </button>
    </div>

    <!-- Nitro Booster Subscription Display -->
    <div v-if="isNitroSubscriber" class="nitro-subscription">
      <div class="row">
        <div class="col-lg-8 offset-lg-2">
          <div class="card nitro-card">
            <div class="card-body p-4">
              <div class="text-center mb-4">
                <div class="nitro-badge mb-3">
                  <feather type="zap" class="nitro-icon"></feather>
                </div>
                <h2 class="nitro-title">Nitro Booster</h2>
                <p class="nitro-plan-name">{{ user.agency.nitro_plan_name || 'Scale Plan' }}</p>
              </div>
              
              <div class="nitro-features">
                <h5 class="mb-3">Your Plan Includes:</h5>
                <ul class="feature-list">
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span><strong>{{ user.agency.free_numbers || 60 }}</strong> Locations/Numbers</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>100% White Labeled</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Landing Page Builder</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Viral Loops Campaigns</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Two-Way Texting</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Automations</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Broadcasts</span>
                  </li>
                  <li>
                    <feather type="check-circle" class="feature-check"></feather>
                    <span>Unlimited Contacts</span>
                  </li>
                </ul>
              </div>

              <div v-if="user.agency.free_messages || user.agency.free_calls" class="nitro-usage mt-4">
                <h5 class="mb-3">Usage Allowances:</h5>
                <div class="usage-grid">
                  <div v-if="user.agency.free_numbers" class="usage-item">
                    <div class="usage-label">Numbers</div>
                    <div class="usage-value">{{ user.agency.usage_numbers || 0 }} / {{ user.agency.free_numbers }}</div>
                  </div>
                  <div v-if="user.agency.free_messages" class="usage-item">
                    <div class="usage-label">Messages</div>
                    <div class="usage-value">{{ formatNumber(user.agency.usage_messages || 0) }} / {{ formatNumber(user.agency.free_messages) }}</div>
                  </div>
                  <div v-if="user.agency.free_calls" class="usage-item">
                    <div class="usage-label">Call Minutes</div>
                    <div class="usage-value">{{ formatNumber(user.agency.usage_calls || 0) }} / {{ formatNumber(user.agency.free_calls) }}</div>
                  </div>
                </div>
              </div>

              <div class="text-center mt-4">
                <p class="text-muted mb-3">Your subscription is managed through the Nitro Booster bundle.</p>
                <b-button variant="primary" @click="goToDashboard">
                  <feather type="arrow-left" class="mr-1"></feather>
                  Go to Dashboard
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isNitroSubscriber && currentStep === 1" class="cart-step">
      <div class="row">
        <div class="col-md-6 offset-md-3">
          <div class="card">
            <div class="card-body">
              <h3 class="h3 text-center mb-4">
                The billing has failed for <span>{{ user.full_name }}</span
                >.<br />
                Please update your card information below to get access.
              </h3>
              <div class="row mb-4 mt-5">
                <div class="col-md-8 offset-md-2">
                  <AddCreditCard ref="cardForm" />
                  <div class="text-center mt-4">
                    <button
                      class="btn btn-primary w-100"
                      :disabled="loadingSubscription"
                      @click="validateCard"
                    >
                      <b-spinner v-if="loadingSubscription" small />
                      <span v-else>ADD PAYMENT METHOD</span>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="paymentMethods && paymentMethods.length" class="text-center pt-4">
                <a
                  href="javascript:void(0)"
                  class="h5 text-primary"
                  @click="onUseExistingPayment"
                >
                  Use existing Payment Method
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isNitroSubscriber && currentStep === 2" class="payment-methods">
      <div class="row">
        <div class="col-md-6 offset-md-3">
          <div class="card">
            <div class="card-body">
              <div v-if="loadingPaymentMethod" class="text-center">
                <b-spinner />
              </div>
              <div v-if="!loadingPaymentMethod">
                <div
                  v-for="payment in paymentMethods"
                  :key="payment.id"
                  class="payment-method"
                >
                  <div class="h5 text-primary text-capitalize">{{
                    payment.brand
                  }}</div>
                  <div class="card-number"
                    >{{ payment.brand }} ****{{ payment.last_four }}</div
                  >
                  <div class="card-expiration"
                    >Expires: {{ payment.exp_month }} /
                    {{ payment.exp_year }}</div
                  >
                  <div class="actions">
                    <button
                      class="btn btn-sm px-sm-4 btn-primary"
                      :disabled="loadingSetDefault"
                      @click="setDefaultPaymentMethod(payment.id)"
                    >
                      <b-spinner v-if="loadingSetDefault" small />
                      <span v-else>Select</span>
                    </button>
                  </div>
                  <hr />
                </div>
              </div>
              <div v-if="!loadingPaymentMethod" class="text-center pt-4">
                <a
                  href="javascript:void(0)"
                  class="h5 text-primary"
                  @click="addPaymentMethod"
                >
                  Add payment method
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isNitroSubscriber && currentStep === 3">
      <div v-if="!loadingPlans" class="row">
        <div v-for="plan in plans" :key="plan.id" class="col-lg-4">
          <div class="card card-pricing">
            <div class="card-body p-4">
              <div class="media">
                <div class="media-body">
                  <h5 class="mt-0 mb-2 font-size-16">{{ plan.name }}</h5>
                  <h2 class="mt-0 mb-2"
                    >${{ plan.amount }}
                    <span class="font-size-14">/ <span class="text-capitalize">{{ plan.interval }}</span></span></h2
                  >
                </div>
                <div class="align-self-center">
                  <feather type="user" />
                </div>
              </div>

              <ul
                class="card-pricing-features text-muted border-top pt-2 mt-2 pl-0 list-unstyled"
              >
                <li
                  v-for="item in plan.features.filter(
                    (f) => f.key === 'Number of Businesses'
                  )"
                  :key="item.key"
                >
                  <i class="uil uil-check text-success font-size-15 mr-1"></i>
                  {{ item.key }}: {{ item.value }}
                </li>
              </ul>

              <div class="mt-5 text-center">
                <button
                  class="btn px-sm-4 btn-primary"
                  :disabled="
                    loadingSubscription !== null || plan.id === user.stripe_plan
                  "
                  @click="onSubscription(plan.id)"
                >
                  <b-spinner v-if="loadingSubscription === plan.id" small />
                  <span v-else>
                    <span v-if="plan.id === user.stripe_plan">Active</span>
                    <span v-else>
                      <i class="uil uil-arrow-right mr-1"></i>Buy Now for ${{
                        plan.amount
                      }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-center mt-5">
        <b-spinner large></b-spinner>
      </div>
    </div>
    <b-modal v-model="showConfirmModal" title="Subscription">
      <p v-if="selectedPlan">Are you sure you want to subscribe the plan <b> {{
        selectedPlan.name
      }}</b>?</p>
      <template v-slot:modal-footer>
        <b-button variant="light" @click="showConfirmModal = false">Cancel</b-button>
        <b-button variant="primary" @click="subscription">
          <b-spinner v-if="loadingSubscription" small />
          <span v-else>
            <i class="uil uil-check"></i> Subscribe
          </span></b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import AddCreditCard from '../Common/AddCreditCard'

export default {
  components: { AddCreditCard },

  data() {
    return {
      loadingPlans: false,
      loadingSubscription: null,
      showAddCard: false,
      loadingCreateCard: false,
      selectedPlanId: null,
      currentStep: 1,
      loadingPaymentMethod: false,
      loadingSetDefault: false,
      loadingStopImpersonate: false,
      showConfirmModal: false,
    }
  },

  computed: {
    isImpersonating() {
      return this.$store.getters['auth/isImpersonating']
    },

    isNitroSubscriber() {
      return this.user && this.user.agency && this.user.agency.is_nitro_subscriber
    },

    plans() {
      let plans = this.$store.getters['subscription/allPlans'] || []
      
      return plans.filter(
        (plan) => plan.visible || plan.id === this.user.stripe_plan
      )
    },

    selectedPlan() {
      return this.plans.find(plan => plan.id === this.selectedPlanId)
    },

    user() {
      return this.$store.getters['auth/user']
    },

    paymentMethods() {
      return this.$store.getters['paymentMethod/all']
    },
  },

  mounted() {
    this.getPaymentMethods();
    if (this.user.default_payment_method && this.user.subscribed) {
      this.getPlans()
    }
  },

  methods: {
    goToDashboard() {
      this.$router.push({ name: 'agency.dashboard' })
    },

    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      }
      if (num >= 1000) {
        return (num / 1000).toFixed(0) + 'K'
      }
      return num.toLocaleString()
    },

    onSubscription(id) {
      this.selectedPlanId = id
      if (this.user && this.user.stripe_status === "active")
      {
        this.showConfirmModal = true
      } else {
        this.subscription();
      }
    },

    logout() {
      this.$store
        .dispatch('auth/logout')
        .then(() => {
          this.$router.push({ name: 'login' })
        })
        .catch(() => {})
    },
    
    onSuccessHandler() {
      if (this.user.agency) {
        this.$router.push({ name: 'agency.businesses.index' })
      } else {
        this.getPlans()
      }
    },

    onFailedHandler() {},

    getPlans() {
      this.currentStep = 3
      if (this.$store.getters['subscription/allPlans']) return

      this.loadingPlans = true
      this.$store
        .dispatch('subscription/getAllPlans')
        .then(() => {
          this.loadingPlans = false
        })
        .catch(() => {
          this.loadingPlans = false
        })
    },

    subscription() {
      this.loadingSubscription = this.selectedPlanId

      this.$store
        .dispatch('subscription/subscribe', this.selectedPlanId)
        .then(() => {
          this.$store
            .dispatch('auth/getProfile')
          this.loadingSubscription = null
          this.showConfirmModal = false
          this.$router.push({ name: 'agency.businesses.index' })
        })
        .catch(() => {
          this.showConfirmModal = false
          this.loadingSubscription = null
          this.currentStep = 1
        })
    },

    addPaymentMethod() {
      this.currentStep = 1
    },

    createPaymentMethod(paymentMethodId) {
      this.$store
        .dispatch('paymentMethod/create', { methodId: paymentMethodId } )
        .then(() => {
          this.$store
            .dispatch('subscription/resume')
            .then(() => {
              this.loadingSubscription = null
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSubscription = null
              if (!this.user.stripe_plan)
              {
                this.getPlans()
              }
            })
        })
        .catch(() => {
          this.loadingSubscription = null
        })
    },

    setDefaultPaymentMethod(paymentMethodId) {
      this.loadingSetDefault = true
      this.$store
        .dispatch('paymentMethod/setDefault', paymentMethodId)
        .then((res) => {
          this.$store
            .dispatch('subscription/resume')
            .then(() => {
              this.loadingSetDefault = false
              this.onSuccessHandler()
            })
            .catch(() => {
              this.loadingSetDefault = false
              if (!this.user.stripe_plan)
              {
                this.getPlans()
              }
            })
        })
        .catch((errors) => {
          this.loadingSetDefault = false
        })
    },

    onUseExistingPayment() {
      this.currentStep = 2
    },

    getPaymentMethods() {
      this.loadingPaymentMethod = true
      this.$store
        .dispatch('paymentMethod/getAll')
        .then((res) => {
          this.loadingPaymentMethod = false
        })
        .catch(() => {
          this.loadingPaymentMethod = false
        })
    },

    async validateCard() {
      this.loadingSubscription = true

      await this.$refs.cardForm
        .validate()
        .then((paymentMethodId) => {
          this.createPaymentMethod(paymentMethodId)
        })
        .catch(() => {
          this.loadingSubscription = null
        })
    },

    stopImpersonate() {
      this.loadingStopImpersonate = true

      this.$store
        .dispatch('auth/stopImpersonate')
        .then(() => {
          this.$store
            .dispatch('auth/getProfile')
            .then((user) => {
              this.$router.push('/admin')
              this.loadingStopImpersonate = false
            })
            .catch(() => {
              this.loadingStopImpersonate = false
            })
        })
        .catch(() => {
          this.loadingStopImpersonate = false
        })
    },
  },
}
</script>

<style lang="scss">
.subscription-page {
  .payment-methods {
    .payment-method {
      padding: 10px 0;
      .card-brand {
        font-size: 16px;
        font-weight: 500;
        color: #2c2e2f;
        text-transform: uppercase;
      }
      .card-bumber,
      .card-expiration {
        font-weight: 500;
        color: #666;
      }
    }
  }
  .payment-methods .payment-method .actions {
    margin-top: 10px;
  }
  .payment-methods .payment-method .actions a[disabled] {
    opacity: 0.6;
  }
  .logout-button {
    max-width: 200px;
    position: absolute;
    top: 50px;
    right: 50px;
  }

  // Nitro Booster Subscription Styles
  .nitro-subscription {
    .nitro-card {
      border: 2px solid #6366f1;
      border-radius: 12px;
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    }

    .nitro-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 64px;
      height: 64px;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      border-radius: 50%;
      
      .nitro-icon {
        width: 32px;
        height: 32px;
        color: #fff;
      }
    }

    .nitro-title {
      font-size: 28px;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 4px;
    }

    .nitro-plan-name {
      font-size: 18px;
      font-weight: 600;
      color: #6366f1;
      margin-bottom: 0;
    }

    .nitro-features {
      background: #fff;
      border-radius: 8px;
      padding: 20px;
      margin-top: 20px;

      h5 {
        color: #334155;
        font-weight: 600;
      }

      .feature-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
        }

        li {
          display: flex;
          align-items: center;
          color: #475569;

          .feature-check {
            width: 18px;
            height: 18px;
            color: #22c55e;
            margin-right: 8px;
            flex-shrink: 0;
          }
        }
      }
    }

    .nitro-usage {
      background: #fff;
      border-radius: 8px;
      padding: 20px;

      h5 {
        color: #334155;
        font-weight: 600;
      }

      .usage-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;

        @media (max-width: 576px) {
          grid-template-columns: 1fr;
        }
      }

      .usage-item {
        text-align: center;
        padding: 12px;
        background: #f8fafc;
        border-radius: 8px;

        .usage-label {
          font-size: 12px;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 4px;
        }

        .usage-value {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }
      }
    }
  }
}
</style>
